import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldError } from '@/components/ui/field-error';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruc: {
    id: string;
    ruc: string;
    razonSocial: string;
    regimen: 'iva_irp' | 'ire_simple';
  };
}

/**
 * Edición del RUC existente del tenant. El NÚMERO del RUC no se puede
 * cambiar (eso requiere cambio fiscal real, lo gestiona el contador).
 * Solo razón social y régimen son editables.
 */
export function AppRucEditDialog({ open, onOpenChange, ruc }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    razonSocial: ruc.razonSocial,
    regimen: ruc.regimen,
  });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (open) {
      setForm({ razonSocial: ruc.razonSocial, regimen: ruc.regimen });
      setIssues({});
    }
  }, [open, ruc.razonSocial, ruc.regimen]);

  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/app/rucs/${ruc.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          razonSocial: form.razonSocial.trim(),
          regimen: form.regimen,
        }),
      }),
    onSuccess: () => {
      toast.success('RUC actualizado');
      qc.invalidateQueries({ queryKey: ['app-me'] });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.status === 400) {
        const body = err.body as { issues?: Record<string, string[]> };
        if (body.issues) {
          setIssues(body.issues);
          return;
        }
      }
      toast.error('No se pudo actualizar el RUC');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Editar mi RUC</DialogTitle>
            <DialogDescription>
              El número del RUC no se puede cambiar — para eso hablá con tu contador. Acá podés
              actualizar la razón social y el régimen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label>RUC</Label>
              <Input value={ruc.ruc} disabled className="font-mono" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="razonSocial" required>
                Razón social
              </Label>
              <Input
                id="razonSocial"
                value={form.razonSocial}
                onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
                autoFocus
              />
              <FieldError error={issues.razonSocial} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="regimen" required>
                Régimen tributario
              </Label>
              <Select
                value={form.regimen}
                onValueChange={(v) => setForm({ ...form, regimen: v as typeof form.regimen })}
              >
                <SelectTrigger id="regimen">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iva_irp">IVA + IRP</SelectItem>
                  <SelectItem value="ire_simple">IRE Simple</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={issues.regimen} />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
